import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { RotateCcw, XCircle, X, User, CalendarDays, Tag, Info, History, ChevronRight, CheckCircle2, Lock, Paperclip, FileText, AlertTriangle } from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/formatters';
import './correction-modal.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Meta = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-2 min-w-0" data-testid={testId}>
    <span className="w-7 h-7 rounded-full bg-white shadow-sm ring-1 ring-slate-200 flex items-center justify-center text-slate-600 flex-shrink-0">
      <Icon className="w-3.5 h-3.5" />
    </span>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 leading-none">{label}</div>
      <div className="text-xs font-semibold text-slate-900 truncate mt-0.5">{value || '—'}</div>
    </div>
  </div>
);

/**
 * QC-CORR-MODAL-001 — premium blocking modal for a QC return / rejection.
 * `locked` = responsible processor who has not acknowledged yet → no close
 * affordance, Escape / backdrop ignored. Everyone else may dismiss freely.
 */
export default function CorrectionRequiredModal({
  open, onOpenChange, data, isReturn, acking, error, onAcknowledge, onContinue, onViewHistory, locked, adminActions,
}) {
  const fb = data?.feedback || {};
  const isAcked = !!data?.qc_feedback_acknowledged_at;
  const title = isReturn ? 'Correction Required' : 'Rejected — Revisions Required';
  const accent = isReturn ? 'text-orange-600' : 'text-red-600';
  const HeroIcon = isReturn ? RotateCcw : XCircle;
  const guard = (e) => { if (locked) e.preventDefault(); };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o && locked) return; onOpenChange(o); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="qc-corr-overlay" data-testid="correction-modal-overlay" />
        <DialogPrimitive.Content
          className="qc-corr-content"
          onEscapeKeyDown={guard}
          onPointerDownOutside={guard}
          onInteractOutside={guard}
          aria-describedby="qc-corr-desc"
          data-testid="correction-required-modal"
          data-locked={locked ? 'true' : 'false'}
          data-acknowledged={isAcked ? 'true' : 'false'}
        >
          {/* Hero */}
          <div className={`qc-corr-hero ${isReturn ? '' : 'qc-corr-hero--rejected'} px-5 pt-4 pb-3.5 relative`}>
            {!locked && (
              <DialogPrimitive.Close
                className="absolute right-3 top-3 w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:bg-white/70 hover:text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="correction-modal-close"
              >
                <X className="w-4 h-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
            <div className="flex items-center gap-3.5 pr-8">
              <span className={`qc-corr-icon w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isReturn ? 'bg-orange-100' : 'bg-red-100'}`}>
                <HeroIcon className={`w-6 h-6 ${accent}`} strokeWidth={2.4} />
              </span>
              <div className="flex-1 min-w-0">
                <div className={`text-[10px] font-bold tracking-[0.14em] ${accent}`}>ACTION REQUIRED</div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <DialogPrimitive.Title className="text-xl font-bold text-slate-900 leading-tight" data-testid="correction-modal-title">
                    {title}
                  </DialogPrimitive.Title>
                  <span className="text-sm text-slate-600" data-testid="correction-modal-iteration">· Iteration {data?.qc_iteration || 1} of QC</span>
                </div>
              </div>
              <div className="hidden sm:block relative w-12 h-12 flex-shrink-0 mr-1" aria-hidden="true">
                <div className="absolute inset-0 rounded-xl bg-white/80 shadow-md ring-1 ring-orange-100 rotate-6 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-orange-200" strokeWidth={1.6} />
                </div>
                <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-lg">
                  <AlertTriangle className="w-3 h-3" strokeWidth={2.6} />
                </span>
              </div>
            </div>
            <DialogPrimitive.Description id="qc-corr-desc" className="text-xs text-slate-700 mt-2">
              {isReturn
                ? 'This order has been returned from QC with items that need to be corrected before you can continue.'
                : 'This order has been rejected by QC and requires revisions before it can continue.'}
            </DialogPrimitive.Description>
          </div>

          {/* Body */}
          <div className="qc-corr-stagger px-5 py-3.5 space-y-3 overflow-y-auto min-h-0 flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Meta icon={User} label="Returned by" value={fb.decided_by_name || 'QC Reviewer'} testId="correction-modal-returned-by" />
              <Meta icon={CalendarDays} label="Date & Time" value={fb.decided_at ? formatDateTimeLocal(fb.decided_at) : null} testId="correction-modal-datetime" />
              <Meta icon={Tag} label="Category" value={(fb.category ? fb.category.replace(/_/g, ' ') : fb.error_type || '').replace(/\b\w/g, (c) => c.toUpperCase())} testId="correction-modal-category" />
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5" data-testid="correction-modal-qc-notes">
              <div className="text-[10px] font-bold tracking-[0.12em] text-slate-500 mb-1">QC NOTES</div>
              <p className="qc-corr-notes text-sm text-slate-900 leading-snug pr-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {fb.reason || 'No notes provided.'}
              </p>
              {fb.typing_context?.reason && (
                <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5" data-testid="correction-modal-typing-context">
                  <div className="text-[10px] font-bold tracking-[0.12em] text-rose-600 mb-0.5">
                    ORIGINAL TYPING RETURN{fb.typing_context.returned_by_name ? ` · ${fb.typing_context.returned_by_name}` : ''}{fb.typing_context.returned_at ? ` · ${formatDateTimeLocal(fb.typing_context.returned_at)}` : ''}
                  </div>
                  <p className="text-xs text-slate-800 leading-snug" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{fb.typing_context.reason}</p>
                </div>
              )}
              {Array.isArray(fb.affected_components) && fb.affected_components.length > 0 && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="feedback-affected-components">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Affected:</span>
                  {fb.affected_components.map((c) => (
                    <span key={c} className="inline-flex items-center text-xs font-medium bg-white border border-orange-300 text-orange-800 rounded-full px-2 py-0.5" data-testid={`feedback-affected-${c}`}>
                      {c === 'mls' ? 'MLS Search' : 'Tax Search'}
                    </span>
                  ))}
                </div>
              )}
              {Array.isArray(fb.evidence_file_ids) && fb.evidence_file_ids.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {fb.evidence_file_ids.map((fid) => (
                    <a key={fid} href={`${API}/files/${fid}/download`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 bg-white border border-slate-200 rounded-full px-2.5 py-1"
                      data-testid={`feedback-evidence-${fid}`}>
                      <Paperclip className="w-3.5 h-3.5" /> Evidence {fid.slice(0, 8)}…
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 flex items-center gap-2.5" data-testid="correction-modal-instruction">
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0"><Info className="w-3 h-3" /></span>
              <p className="text-xs text-slate-800 leading-snug">
                <span className="font-semibold">Address the QC items above before continuing.</span>{' '}
                <span className="text-slate-600">{isAcked ? 'Edit the information and re-submit for QC once corrected.' : 'Acknowledge to unlock the workspace, then re-submit for QC once corrected.'}</span>
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button type="button" onClick={onViewHistory}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800 hover:text-slate-950 underline underline-offset-4 decoration-slate-300 hover:decoration-slate-600"
                data-testid="processor-feedback-view-history-btn">
                <History className="w-4 h-4" /> View history ({data?.history_count ?? 0}) <ChevronRight className="w-3.5 h-3.5" />
              </button>
              {isAcked ? (
                <div className="flex items-center gap-2" data-testid="correction-modal-acknowledged">
                  <span className="qc-corr-ack-pop inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-sm font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> Acknowledged
                  </span>
                  <span className="text-[11px] text-slate-500">{formatDateTimeLocal(data.qc_feedback_acknowledged_at)}</span>
                </div>
              ) : data?.can_acknowledge ? (
                <button type="button" onClick={onAcknowledge} disabled={acking}
                  className={`qc-corr-cta inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${isReturn ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="processor-feedback-acknowledge-btn">
                  <CheckCircle2 className="w-4 h-4" /> {acking ? 'Acknowledging…' : 'Acknowledge'}
                </button>
              ) : (
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-600 px-3 py-1 text-xs font-medium" data-testid="correction-modal-not-acknowledged">
                  Awaiting processor acknowledgement
                </span>
              )}
            </div>
            {error && <p className="text-xs text-red-600" data-testid="correction-modal-error">{error}</p>}

            {adminActions && (
              <div className="rounded-lg border border-dashed border-red-200 bg-red-50/40 px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="correction-modal-admin-actions">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold text-red-600 uppercase tracking-wide">Admin Actions</span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Lock className="w-3 h-3" /> Admin only · use with caution</span>
                </div>
                <div className="[&>*]:mt-0 [&_button]:h-8 [&_button]:text-xs">{adminActions}</div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 pb-4 pt-2 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
            {!isAcked && !locked && (
              <DialogPrimitive.Close className="qc-corr-cta rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200" data-testid="correction-modal-dismiss">
                Close
              </DialogPrimitive.Close>
            )}
            <button type="button" onClick={onContinue} disabled={!isAcked}
              title={isAcked ? undefined : 'Acknowledge the QC feedback first'}
              className="qc-corr-cta inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="correction-modal-continue-btn">
              Continue to Edit <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
